import { useEffect, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  PhoneCall, MessageSquare, User, MapPin, IndianRupee, Clock,
  CheckCircle2, XCircle, PhoneMissed, Home as HomeIcon, Sparkles,
} from "lucide-react";

const STATUS_META = {
  new:            { label: "New",           badge: "bg-blue-500/20 text-blue-300 border-blue-500/30" },
  contacted:      { label: "Contacted",     badge: "bg-amber-500/20 text-amber-300 border-amber-500/30" },
  interested:     { label: "Interested",    badge: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30" },
  callback:       { label: "Callback",      badge: "bg-fuchsia-500/20 text-fuchsia-300 border-fuchsia-500/30" },
  not_interested: { label: "Not interested",badge: "bg-gray-500/20 text-gray-400 border-gray-500/30" },
  rented:         { label: "Rented",        badge: "bg-[#C9A24A]/25 text-[#E1BC5F] border-[#C9A24A]/40" },
};

function _timeAgoShort(iso) {
  if (!iso) return "";
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days <= 0) {
    const hrs = Math.floor((Date.now() - new Date(iso).getTime()) / 3600000);
    return hrs <= 0 ? "just now" : `${hrs}h ago`;
  }
  if (days === 1) return "1d ago";
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

function _formatPrice(n, isRent) {
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(2)} L`;
  return `₹${n.toLocaleString("en-IN")}${isRent ? "/mo" : ""}`;
}

export default function StaffDashboardPage() {
  const { me, refresh } = useOutletContext();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: "all", assigned_to: "all", city: "", listing_type: "all" });
  const [selected, setSelected] = useState(null); // lead being edited

  const load = useCallback(() => {
    setLoading(true);
    const q = {};
    if (filter.status !== "all") q.status = filter.status;
    if (filter.assigned_to !== "all") q.assigned_to = filter.assigned_to;
    if (filter.city) q.city = filter.city;
    if (filter.listing_type !== "all") q.listing_type = filter.listing_type;
    Promise.all([
      api.get("/staff/leads", { params: q }),
      api.get("/staff/leads/stats"),
    ]).then(([leadsRes, statsRes]) => {
      setLeads(leadsRes.data);
      setStats(statsRes.data);
    }).finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const claim = async (lead) => {
    try {
      await api.post(`/staff/leads/${lead.id}/claim`);
      toast.success("Lead assigned to you");
      load(); refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not claim lead");
    }
  };

  return (
    <div data-testid="staff-dashboard-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <p className="text-[11px] uppercase tracking-[0.25em] text-[#C9A24A] font-bold mb-2">Enquiries pipeline</p>
          <h1 className="font-display text-4xl text-white mb-1">Leads inbox</h1>
          <p className="text-sm text-gray-500">
            Every tenant who reveals contact info lands here. Assign, call, and log outcomes.
          </p>
        </div>
        <div className="text-right">
          <p className="text-[11px] uppercase tracking-widest text-gray-500">Signed in as</p>
          <p className="font-display text-lg text-white">{me.name}</p>
          <p className="text-xs text-[#C9A24A] font-bold uppercase tracking-widest">{me.role}</p>
        </div>
      </div>

      {/* Stats strip */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-8" data-testid="staff-stats-strip">
          {[
            { key: "total", label: "Total", icon: MessageSquare },
            { key: "new", label: "New", icon: Sparkles },
            { key: "contacted", label: "Contacted", icon: PhoneCall },
            { key: "interested", label: "Interested", icon: CheckCircle2 },
            { key: "callback", label: "Callback", icon: Clock },
            { key: "not_interested", label: "Rejected", icon: XCircle },
            { key: "rented", label: "Rented", icon: HomeIcon },
          ].map(({ key, label, icon: Icon }) => (
            <div
              key={key}
              className={`rounded-xl bg-[#0F1420] border p-4 cursor-pointer transition-all hover:border-[#C9A24A]/40 ${
                filter.status === key ? "border-[#C9A24A]" : "border-white/5"
              }`}
              onClick={() => setFilter({ ...filter, status: key === "total" ? "all" : key })}
              data-testid={`stat-${key}`}
            >
              <div className="flex items-center justify-between mb-2">
                <Icon size={14} className="text-gray-500" />
                <span className="text-2xl font-display text-white">{stats[key] || 0}</span>
              </div>
              <p className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">{label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <Select value={filter.assigned_to} onValueChange={(v) => setFilter({ ...filter, assigned_to: v })}>
          <SelectTrigger data-testid="filter-assigned" className="w-44 h-10 bg-[#0F1420] border-white/10 text-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All assignments</SelectItem>
            <SelectItem value="me">Assigned to me</SelectItem>
            <SelectItem value="unassigned">Unassigned</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filter.listing_type} onValueChange={(v) => setFilter({ ...filter, listing_type: v })}>
          <SelectTrigger data-testid="filter-type" className="w-40 h-10 bg-[#0F1420] border-white/10 text-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Rent & Sale</SelectItem>
            <SelectItem value="rent">Rent only</SelectItem>
            <SelectItem value="sale">Sale only</SelectItem>
          </SelectContent>
        </Select>
        <Input
          data-testid="filter-city"
          value={filter.city}
          onChange={(e) => setFilter({ ...filter, city: e.target.value })}
          placeholder="City filter"
          className="w-40 h-10 bg-[#0F1420] border-white/10 text-white placeholder:text-gray-600"
        />
      </div>

      {/* Leads list */}
      {loading ? (
        <div className="py-20 text-center text-gray-500 text-sm">Loading leads…</div>
      ) : leads.length === 0 ? (
        <div className="py-20 text-center bg-[#0F1420] border border-white/5 rounded-2xl">
          <MessageSquare size={28} className="text-gray-600 mx-auto mb-3" />
          <p className="font-display text-xl text-white mb-1">No leads match these filters</p>
          <p className="text-sm text-gray-500">New enquiries will appear here as tenants reveal owner contact.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="leads-list">
          {leads.map((lead) => {
            const status = STATUS_META[lead.call_status] || STATUS_META.new;
            const isMine = lead.assigned_to === me.id;
            return (
              <div
                key={lead.id}
                data-testid={`lead-row-${lead.id}`}
                className={`bg-[#0F1420] border rounded-xl p-4 transition-all hover:border-[#C9A24A]/40 ${
                  isMine ? "border-[#7A1E2A]/50" : "border-white/5"
                }`}
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  {/* Tenant */}
                  <div className="flex-1 min-w-[220px]">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest border ${status.badge}`}>
                        {status.label}
                      </span>
                      {isMine && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-[#7A1E2A] text-white">
                          Mine
                        </span>
                      )}
                      <span className="text-[10px] text-gray-500 uppercase tracking-widest">
                        <Clock size={9} className="inline mr-1" />{_timeAgoShort(lead.revealed_at)}
                      </span>
                    </div>
                    <p className="font-display text-lg text-white truncate">{lead.tenant_name || "Anonymous tenant"}</p>
                    <div className="flex items-center gap-3 text-xs text-gray-400 mt-1">
                      {lead.tenant_phone && (
                        <a href={`tel:${lead.tenant_phone}`} data-testid={`lead-call-${lead.id}`} className="inline-flex items-center gap-1 text-[#C9A24A] hover:underline">
                          <PhoneCall size={11} /> {lead.tenant_phone}
                        </a>
                      )}
                      {lead.tenant_email && (
                        <a href={`mailto:${lead.tenant_email}`} className="inline-flex items-center gap-1 hover:text-white">
                          {lead.tenant_email}
                        </a>
                      )}
                    </div>
                  </div>

                  {/* Property */}
                  <div className="flex-1 min-w-[220px]">
                    <p className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Interested in</p>
                    <a
                      href={`/property/${lead.property_id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-sm text-white hover:text-[#C9A24A] transition-colors truncate block"
                    >
                      {lead.property_title}
                    </a>
                    <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                      <span className="inline-flex items-center gap-1">
                        <MapPin size={10} /> {lead.property_locality}, {lead.property_city}
                      </span>
                      <span className="inline-flex items-center gap-1 text-[#E1BC5F] font-semibold">
                        <IndianRupee size={10} /> {_formatPrice(lead.property_price, lead.property_listing_type === "rent")}
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap gap-2 items-center">
                    {!lead.assigned_to && (
                      <Button
                        data-testid={`lead-claim-${lead.id}`}
                        onClick={() => claim(lead)}
                        className="h-8 px-3 rounded-lg bg-[#7A1E2A] hover:bg-[#5C1420] text-white text-xs font-semibold"
                      >
                        Claim
                      </Button>
                    )}
                    <Button
                      data-testid={`lead-log-${lead.id}`}
                      onClick={() => setSelected(lead)}
                      variant="outline"
                      className="h-8 px-3 rounded-lg bg-transparent border-white/10 text-gray-300 hover:bg-white/5 hover:text-white text-xs"
                    >
                      Log call
                    </Button>
                  </div>
                </div>

                {lead.notes && (
                  <p className="mt-3 pt-3 border-t border-white/5 text-xs text-gray-400 italic">
                    “{lead.notes}”
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {selected && (
        <LogCallDialog
          lead={selected}
          onClose={() => setSelected(null)}
          onSaved={() => { setSelected(null); load(); refresh(); }}
        />
      )}
    </div>
  );
}


function LogCallDialog({ lead, onClose, onSaved }) {
  const [status, setStatus] = useState(lead.call_status || "contacted");
  const [notes, setNotes] = useState(lead.notes || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/staff/leads/${lead.id}`, { call_status: status, notes });
      toast.success("Call logged");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0F1420] border-white/10 text-white max-w-md" data-testid="log-call-dialog">
        <DialogHeader>
          <DialogTitle className="text-white font-display text-xl">Log call outcome</DialogTitle>
          <p className="text-xs text-gray-500">
            {lead.tenant_name} · {lead.property_title}
          </p>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-gray-400 mb-2 block">Outcome</label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { v: "contacted",     label: "Contacted",      color: "hover:bg-amber-500/20 hover:border-amber-500/50" },
                { v: "interested",    label: "Interested",     color: "hover:bg-emerald-500/20 hover:border-emerald-500/50" },
                { v: "callback",      label: "Callback later", color: "hover:bg-fuchsia-500/20 hover:border-fuchsia-500/50" },
                { v: "not_interested",label: "Not interested", color: "hover:bg-gray-500/20 hover:border-gray-500/50" },
                { v: "rented",        label: "Rented",         color: "hover:bg-[#C9A24A]/25 hover:border-[#C9A24A]" },
                { v: "new",           label: "Reset to new",   color: "hover:bg-blue-500/20 hover:border-blue-500/50" },
              ].map(({ v, label, color }) => (
                <button
                  key={v}
                  data-testid={`log-outcome-${v}`}
                  onClick={() => setStatus(v)}
                  className={`px-3 py-2 rounded-lg border text-sm text-left transition-colors ${
                    status === v
                      ? "bg-[#7A1E2A] border-[#7A1E2A] text-white font-semibold"
                      : `bg-[#0B0F17] border-white/10 text-gray-300 ${color}`
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-gray-400 mb-2 block">Notes</label>
            <Textarea
              data-testid="log-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="What was discussed? Any callback time?"
              className="bg-[#0B0F17] border-white/10 text-white placeholder:text-gray-600 resize-none"
            />
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={onClose} className="text-gray-400 hover:text-white hover:bg-white/5">Cancel</Button>
            <Button
              data-testid="log-save"
              onClick={save}
              disabled={busy}
              className="bg-gradient-to-r from-[#C9A24A] to-[#A8842E] text-[#1B0F14] font-bold hover:brightness-110"
            >
              {busy ? "Saving…" : "Save log"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
